import { useCallback, useRef, useState } from "react";
import Contexto from "./Ctx";
import { message } from "antd";
import { EVENT_BLE } from "../../Constantes";
import useSesion from "../../Hooks/Sesion";
import { avisoBluetoothNoDisponible } from "./BleConexionControles";

const uuid_service = "00000001-710e-4a5b-8d75-3e5b444bc3cf";
const uuid_caracteristica_peso = "00000002-710e-4a5b-8d75-3e5b444bc3cf";
const uuid_caracteristica_resetear = "00000003-710e-4a5b-8d75-3e5b444bc3cf";

function onPeso(event) {
    const peso = new TextDecoder().decode(event.target.value);
    const _data = peso.split(",");
    const disparador = new CustomEvent(EVENT_BLE, {
        detail: [parseFloat(_data[0]), parseFloat(_data[1]), parseFloat(_data[2])],
    });
    document.dispatchEvent(disparador);
}

const BleProveedor = ({ children }) => {
    const { usuario } = useSesion();
    const esAdmin = String(usuario?.rol ?? "").toLowerCase() === "admin";
    const simulacionVisible = Boolean(usuario);

    const [dispositivo, setDispositivo] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [data] = useState([]);
    const [simulacionSinDispositivo, setSimulacionSinDispositivo] = useState(false);
    const [modalPruebaAbierto, setModalPruebaAbierto] = useState(false);
    const [sesionDesdeAtletas, setSesionDesdeAtletas] = useState(false);
    const [tipoPruebaSesion, setTipoPruebaSesion] = useState(null);

    const dispositivoRef = useRef(null);
    const caracteristicaPesoRef = useRef(null);
    const caracteristica_resetear = useRef(null);
    const preservarSesionAtletasRef = useRef(false);

    const desconectar = useCallback(async () => {
        const char = caracteristicaPesoRef.current;
        const dev = dispositivoRef.current;

        if (char) {
            try {
                char.removeEventListener("characteristicvaluechanged", onPeso);
                await char.stopNotifications();
            } catch (err) {
                console.log(err);
            }
        }

        if (dev?.gatt?.connected) {
            try {
                dev.gatt.disconnect();
            } catch (err) {
                console.log(err);
            }
        }

        caracteristicaPesoRef.current = null;
        caracteristica_resetear.current = null;
        dispositivoRef.current = null;
        setDispositivo(null);
        setSimulacionSinDispositivo(false);
        setModalPruebaAbierto(false);
    }, []);

    async function resetearBalanza() {
        if (caracteristica_resetear.current) {
            try {
                const encoder = new TextEncoder("utf-8");
                await caracteristica_resetear.current.writeValue(encoder.encode("R"));
            } catch (err) {
                console.log(err);
            }
        }
    }

    const marcarConectado = useCallback((device) => {
        dispositivoRef.current = device;
        setDispositivo(device);
        setModalPruebaAbierto(false);
    }, []);

    const conectar = useCallback(async () => {
        if (!navigator.bluetooth) {
            avisoBluetoothNoDisponible();
            return;
        }
        try {
            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: [uuid_service],
            });
            setCargando(true);
            const server = await device.gatt.connect();
            const servicio = await server.getPrimaryService(uuid_service);
            const caracteristica = await servicio.getCharacteristic(uuid_caracteristica_peso);

            try {
                const caracteristicaReset = await servicio.getCharacteristic(uuid_caracteristica_resetear);
                caracteristica_resetear.current = caracteristicaReset;
            } catch {
                caracteristica_resetear.current = null;
            }

            await caracteristica.startNotifications();
            caracteristica.addEventListener("characteristicvaluechanged", onPeso);
            caracteristicaPesoRef.current = caracteristica;

            setSimulacionSinDispositivo(false);
            marcarConectado(device);
        } catch (err) {
            const nombre = err?.name || "";
            if (nombre === "NotFoundError") {
                message.info("Selección Bluetooth cancelada. Si no tienes hardware, usa «Generar datos simulados».");
            } else {
                console.log(err);
                message.error(err?.message || "No se pudo conectar por Bluetooth.");
            }
        } finally {
            setCargando(false);
        }
    }, [marcarConectado]);

    const continuarSimulacion = useCallback(() => {
        setSimulacionSinDispositivo(true);
        marcarConectado({ simulado: true });
        message.success("Modo simulación activo: al pulsar «Iniciar prueba» se inyectarán datos sintéticos.");
    }, [marcarConectado]);

    const omitirSoloBluetooth = useCallback(() => {
        setSimulacionSinDispositivo(false);
        marcarConectado({ omitido: true });
    }, [marcarConectado]);

    const solicitarConexionPrueba = useCallback(() => {
        setModalPruebaAbierto(true);
    }, []);

    const cerrarModalPrueba = useCallback(() => {
        setModalPruebaAbierto(false);
        omitirSoloBluetooth();
    }, [omitirSoloBluetooth]);

    const iniciarSesionAtletas = useCallback(
        (tipo) => {
            if (!tipo) return;
            setTipoPruebaSesion(tipo);
            setSesionDesdeAtletas(true);
        },
        []
    );

    const finalizarSesionAtletas = useCallback(async () => {
        await desconectar();
        setSesionDesdeAtletas(false);
        setTipoPruebaSesion(null);
        preservarSesionAtletasRef.current = false;
    }, [desconectar]);

    const marcarNavegacionAPrueba = useCallback(() => {
        preservarSesionAtletasRef.current = true;
    }, []);

    const debePreservarSesionAtletas = useCallback(() => preservarSesionAtletasRef.current, []);

    const limpiarPreservarSesionAtletas = useCallback(() => {
        preservarSesionAtletasRef.current = false;
    }, []);

    const conectado = Boolean(dispositivo);

    const value = {
        data,
        dispositivo,
        conectado,
        cargando,
        simulacionSinDispositivo,
        simulacionVisible,
        esAdmin,
        sesionDesdeAtletas,
        tipoPruebaSesion,
        modalPruebaAbierto,
        resetearBalanza,
        conectar,
        desconectar,
        continuarSimulacion,
        omitirSoloBluetooth,
        solicitarConexionPrueba,
        cerrarModalPrueba,
        iniciarSesionAtletas,
        finalizarSesionAtletas,
        marcarNavegacionAPrueba,
        debePreservarSesionAtletas,
        limpiarPreservarSesionAtletas,
    };

    return <Contexto.Provider value={value}>{children}</Contexto.Provider>;
};

export default BleProveedor;
